<template>
  <div class="live-event-manager">
    <div class="manager-header">
      <h2>Live Events</h2>
      <button class="btn btn-primary" @click="openCreate">
        <i class="fas fa-plus"></i> Schedule event
      </button>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading events…</p>
    </div>

    <table v-else-if="events.length" class="events-table">
      <thead>
        <tr>
          <th>Title</th>
          <th>Course</th>
          <th>Speaker</th>
          <th>When</th>
          <th>Status</th>
          <th>CEU</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="ev in events" :key="ev.id">
          <td>{{ ev.title }}</td>
          <td>{{ ev.course_title }}</td>
          <td>{{ ev.speaker_name }}</td>
          <td>{{ formatDate(ev.scheduled_start) }} → {{ formatDate(ev.scheduled_end) }}</td>
          <td>
            <span class="status-badge" :class="ev.status">{{ statusLabel(ev.status) }}</span>
          </td>
          <td>{{ ev.grants_ceu ? `${ev.ceu_credit} CEU` : '—' }}</td>
          <td class="actions">
            <router-link :to="`/learning/live-events/${ev.id}`" class="btn-small">
              Open
            </router-link>
            <button class="btn-small btn-secondary" @click="openEdit(ev)">Edit</button>
            <button v-if="ev.status !== 'cancelled' && ev.status !== 'ended'" class="btn-small btn-danger" @click="cancelEvent(ev)">Cancel</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="empty-state">
      <i class="fas fa-calendar-alt"></i>
      <h3>No live events yet</h3>
      <p>Schedule a webinar or live Q&amp;A to broadcast to enrolled learners.</p>
      <button class="btn btn-primary" @click="openCreate">
        <i class="fas fa-plus"></i> Schedule your first event
      </button>
    </div>

    <!-- Create / Edit modal -->
    <div v-if="showForm" class="modal-backdrop" @click.self="closeForm">
      <div class="modal">
        <header>
          <h3>{{ editing ? 'Edit live event' : 'Schedule live event' }}</h3>
          <button class="close" @click="closeForm">&times;</button>
        </header>
        <form class="form" @submit.prevent="saveForm">
          <label>
            Title
            <input v-model="form.title" required maxlength="255" />
          </label>
          <label>
            Course
            <select v-model.number="form.course" required>
              <option :value="null" disabled>Select a course…</option>
              <option v-for="c in courseOptions" :key="c.id" :value="c.id">{{ c.title }}</option>
            </select>
          </label>
          <label>
            Speaker
            <div class="speaker-picker">
              <input
                type="text"
                :value="speakerSearch"
                placeholder="Search speakers by name, credentials, or topic…"
                @input="onSpeakerSearch($event.target.value)"
                @focus="speakerDropdownOpen = true"
                @blur="closeSpeakerDropdownSoon"
              />
              <div v-if="form.speaker && currentSpeakerLabel" class="speaker-selected">
                Selected: <strong>{{ currentSpeakerLabel }}</strong>
                <button type="button" class="link" @click="clearSpeaker">Change</button>
              </div>
              <div v-if="speakerDropdownOpen" class="speaker-dropdown">
                <div v-if="speakerLoading" class="dropdown-status">Searching…</div>
                <button
                  v-for="s in speakerSearchResults"
                  :key="s.id"
                  type="button"
                  class="dropdown-item"
                  @mousedown.prevent="selectSpeaker(s)"
                >
                  <div class="dropdown-name">
                    {{ s.full_name }}
                    <span v-if="s.credentials" class="muted">— {{ s.credentials }}</span>
                  </div>
                  <div class="dropdown-meta">{{ s.email }}</div>
                </button>
                <button
                  v-if="speakerSearch && !speakerLoading"
                  type="button"
                  class="dropdown-invite"
                  @mousedown.prevent="openInlineInvite()"
                >
                  <i class="fas fa-user-plus"></i>
                  Invite missing speaker for "{{ speakerSearch }}"
                </button>
                <div v-if="!speakerLoading && !speakerSearchResults.length && !speakerSearch" class="dropdown-status">
                  Type a name to search the speaker pool.
                </div>
              </div>
            </div>
          </label>
          <div class="row">
            <label>
              Starts at
              <input v-model="form.scheduled_start" type="datetime-local" required />
            </label>
            <label>
              Ends at
              <input v-model="form.scheduled_end" type="datetime-local" required />
            </label>
          </div>
          <label>
            Timezone
            <input v-model="form.timezone" placeholder="America/New_York" />
          </label>
          <label>
            Description
            <textarea v-model="form.description" rows="3"></textarea>
          </label>
          <div class="row">
            <label class="checkbox">
              <input type="checkbox" v-model="form.grants_ceu" />
              Grants CEU
            </label>
            <label v-if="form.grants_ceu">
              CEU hours
              <input v-model.number="form.ceu_credit" type="number" step="0.25" min="0" />
            </label>
            <label>
              Completion threshold (%)
              <input v-model.number="form.completion_threshold_pct" type="number" min="1" max="100" />
            </label>
          </div>
          <label>
            Max attendees
            <input v-model.number="form.max_attendees" type="number" min="1" />
          </label>

          <div v-if="formError" class="form-error">{{ formError }}</div>

          <footer>
            <button type="button" class="btn-secondary" @click="closeForm">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="saving">
              {{ saving ? 'Saving…' : (editing ? 'Save changes' : 'Schedule event') }}
            </button>
          </footer>
        </form>
      </div>
    </div>

    <!-- Inline invite-speaker modal (Phase 2 — single-click invite from picker) -->
    <div v-if="showInlineInvite" class="modal-backdrop" @click.self="closeInlineInvite">
      <div class="modal" style="width: 480px;">
        <header>
          <h3>Invite Speaker</h3>
          <button class="close" @click="closeInlineInvite">&times;</button>
        </header>
        <form class="form" @submit.prevent="submitInlineInvite">
          <label>
            Email <span class="muted">*</span>
            <input v-model="inlineInvite.email" type="email" required />
          </label>
          <div class="row">
            <label>
              First name
              <input v-model="inlineInvite.first_name" type="text" />
            </label>
            <label>
              Last name
              <input v-model="inlineInvite.last_name" type="text" />
            </label>
          </div>
          <label>
            Credentials
            <input v-model="inlineInvite.credentials" type="text" placeholder="DVM, DACVIM" />
          </label>
          <label class="checkbox">
            <input v-model="inlineInvite.send_email" type="checkbox" />
            Send welcome email with login credentials
          </label>
          <div v-if="inlineInviteError" class="form-error">{{ inlineInviteError }}</div>
          <footer>
            <button type="button" class="btn-secondary" @click="closeInlineInvite">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="inlineInviting">
              {{ inlineInviting ? 'Inviting…' : 'Invite & Select' }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import liveEventService from '@/services/liveEventService';

const emptyForm = () => ({
  title: '',
  description: '',
  course: null,
  speaker: null,
  scheduled_start: '',
  scheduled_end: '',
  timezone: 'America/New_York',
  grants_ceu: false,
  ceu_credit: 1,
  completion_threshold_pct: 75,
  max_attendees: 100,
  provider: 'livekit',
});

export default {
  name: 'LiveEventManager',
  data() {
    return {
      loading: true,
      events: [],
      courseOptions: [],
      userOptions: [],
      showForm: false,
      editing: null,
      form: emptyForm(),
      saving: false,
      formError: '',
      // Phase 2 — speaker drawdown
      speakerSearch: '',
      speakerSearchResults: [],
      speakerLoading: false,
      speakerDropdownOpen: false,
      speakerDebounce: null,
      closeSpeakerTimer: null,
      knownSpeakers: {}, // id → speaker payload, used to render selected label
      // Inline invite
      showInlineInvite: false,
      inlineInvite: { email: '', first_name: '', last_name: '', credentials: '', send_email: true },
      inlineInviting: false,
      inlineInviteError: '',
    };
  },
  computed: {
    currentSpeakerLabel() {
      const id = this.form.speaker;
      if (!id) return '';
      const known = this.knownSpeakers[id];
      if (known) return known.full_name + (known.credentials ? ` — ${known.credentials}` : '');
      const fallback = this.userOptions.find(u => u.id === id);
      return fallback ? (fallback.full_name || fallback.email) : `User #${id}`;
    },
  },
  mounted() {
    this.refresh();
    this.loadOptions();
  },
  methods: {
    async refresh() {
      this.loading = true;
      try {
        const data = await liveEventService.list();
        this.events = Array.isArray(data) ? data : (data.results || []);
      } catch (err) {
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    async loadOptions() {
      try {
        const courses = await api.get('/lms/courses/', { params: { page_size: 200 } }).then(r => r.data);
        this.courseOptions = Array.isArray(courses) ? courses : (courses.results || []);
      } catch (err) { console.warn('Failed to load courses', err); }
      try {
        const users = await api.get('/users/organization/').then(r => r.data);
        const list = Array.isArray(users) ? users : (users.results || []);
        this.userOptions = list
          .filter(u => u.is_active !== false)
          .map(u => ({
            id: u.id,
            email: u.email,
            full_name: u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim(),
          }));
      } catch (err) {
        console.warn('Failed to load users (non-fatal)', err);
        this.userOptions = [];
      }
    },
    openCreate() {
      this.editing = null;
      this.form = emptyForm();
      this.formError = '';
      this.showForm = true;
    },
    openEdit(ev) {
      this.editing = ev;
      this.form = {
        ...emptyForm(),
        title: ev.title,
        description: ev.description || '',
        course: ev.course,
        speaker: ev.speaker,
        scheduled_start: this.toLocalInput(ev.scheduled_start),
        scheduled_end: this.toLocalInput(ev.scheduled_end),
        timezone: ev.timezone,
        grants_ceu: ev.grants_ceu,
        ceu_credit: Number(ev.ceu_credit || 0),
        completion_threshold_pct: ev.completion_threshold_pct || 75,
        max_attendees: ev.max_attendees || 100,
        provider: ev.provider || 'livekit',
      };
      this.formError = '';
      this.showForm = true;
    },
    closeForm() {
      this.showForm = false;
      this.editing = null;
    },
    async saveForm() {
      this.saving = true;
      this.formError = '';
      const payload = {
        ...this.form,
        scheduled_start: new Date(this.form.scheduled_start).toISOString(),
        scheduled_end: new Date(this.form.scheduled_end).toISOString(),
      };
      try {
        if (this.editing) {
          await liveEventService.update(this.editing.id, payload);
        } else {
          await liveEventService.create(payload);
        }
        this.closeForm();
        this.refresh();
      } catch (err) {
        this.formError = err?.response?.data?.detail
          || JSON.stringify(err?.response?.data || {})
          || err.message;
      } finally {
        this.saving = false;
      }
    },
    async cancelEvent(ev) {
      if (!confirm(`Cancel "${ev.title}"?`)) return;
      try {
        await liveEventService.cancel(ev.id);
        this.refresh();
      } catch (err) {
        alert(err?.response?.data?.detail || 'Cancellation failed');
      }
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
      } catch (_) { return iso; }
    },

    // ── Speaker drawdown (Phase 2) ────────────────────────────────────
    onSpeakerSearch(value) {
      this.speakerSearch = value;
      this.speakerDropdownOpen = true;
      if (this.speakerDebounce) clearTimeout(this.speakerDebounce);
      this.speakerDebounce = setTimeout(() => this.searchSpeakers(), 200);
    },
    async searchSpeakers() {
      this.speakerLoading = true;
      try {
        const { data } = await api.get('/users/speakers/', {
          params: { q: this.speakerSearch, page_size: 10 },
        });
        this.speakerSearchResults = data.results || [];
        for (const s of this.speakerSearchResults) this.knownSpeakers[s.id] = s;
      } catch (err) {
        console.warn('speaker search failed', err);
        this.speakerSearchResults = [];
      } finally {
        this.speakerLoading = false;
      }
    },
    selectSpeaker(s) {
      this.form.speaker = s.id;
      this.knownSpeakers[s.id] = s;
      this.speakerSearch = '';
      this.speakerDropdownOpen = false;
    },
    clearSpeaker() {
      this.form.speaker = null;
    },
    closeSpeakerDropdownSoon() {
      // Delay so a click on a dropdown item still fires before closing.
      if (this.closeSpeakerTimer) clearTimeout(this.closeSpeakerTimer);
      this.closeSpeakerTimer = setTimeout(() => { this.speakerDropdownOpen = false; }, 150);
    },
    openInlineInvite() {
      const guess = this.speakerSearch.trim();
      const looksLikeEmail = guess.includes('@');
      const parts = looksLikeEmail ? [] : guess.split(/\s+/);
      this.inlineInvite = {
        email: looksLikeEmail ? guess : '',
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' '),
        credentials: '',
        send_email: true,
      };
      this.inlineInviteError = '';
      this.showInlineInvite = true;
    },
    closeInlineInvite() {
      this.showInlineInvite = false;
    },
    async submitInlineInvite() {
      this.inlineInviting = true;
      this.inlineInviteError = '';
      try {
        const { data } = await api.post('/users/speakers/invite/', this.inlineInvite);
        this.selectSpeaker(data);
        this.closeInlineInvite();
      } catch (err) {
        this.inlineInviteError =
          err?.response?.data?.detail || err.message || 'Invite failed';
      } finally {
        this.inlineInviting = false;
      }
    },

    toLocalInput(iso) {
      if (!iso) return '';
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    statusLabel(s) {
      return { scheduled: 'Scheduled', live: 'LIVE', ended: 'Ended', cancelled: 'Cancelled' }[s] || s;
    },
  },
};
</script>

<style scoped>
.live-event-manager { padding: 1rem; }
.manager-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.manager-header h2 { margin: 0; }
.btn, .btn-primary, .btn-secondary, .btn-danger, .btn-small {
  padding: 0.5rem 0.85rem; border-radius: 6px; border: none; cursor: pointer; font-weight: 600;
}
.btn-primary { background: #2563eb; color: white; }
.btn-secondary { background: #e5e7eb; color: #111827; }
.btn-danger { background: #dc2626; color: white; }
.btn-small { padding: 0.3rem 0.6rem; font-size: 0.85rem; text-decoration: none; display: inline-block; background: #2563eb; color: white; }
.loading { padding: 2rem; text-align: center; }
.spinner { width: 30px; height: 30px; border: 3px solid #e5e7eb; border-top-color: #2563eb; border-radius: 50%; margin: 0 auto 0.75rem; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.events-table { width: 100%; border-collapse: collapse; }
.events-table th, .events-table td { padding: 0.6rem 0.75rem; text-align: left; border-bottom: 1px solid #e5e7eb; font-size: 0.9rem; }
.events-table th { background: #f9fafb; color: #4b5563; }
.events-table .actions { display: flex; gap: 0.4rem; }
.status-badge { padding: 0.15rem 0.5rem; border-radius: 10px; font-size: 0.75rem; font-weight: 700; }
.status-badge.scheduled { background: #dbeafe; color: #1d4ed8; }
.status-badge.live { background: #fee2e2; color: #b91c1c; }
.status-badge.ended { background: #e5e7eb; color: #4b5563; }
.status-badge.cancelled { background: #fef3c7; color: #92400e; }
.empty-state { text-align: center; padding: 3rem 1rem; color: #6b7280; }
.empty-state i { font-size: 2.5rem; color: #9ca3af; margin-bottom: 0.75rem; display: block; }

.modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: white; border-radius: 8px; width: 560px; max-width: 95vw; max-height: 90vh; overflow: auto; }
.modal header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.25rem; border-bottom: 1px solid #e5e7eb; }
.modal header h3 { margin: 0; }
.modal .close { border: none; background: transparent; font-size: 1.4rem; cursor: pointer; color: #6b7280; }
.form { padding: 1rem 1.25rem; display: flex; flex-direction: column; gap: 0.75rem; }
.form label { display: flex; flex-direction: column; gap: 0.25rem; font-size: 0.85rem; color: #374151; }
.form label.checkbox { flex-direction: row; align-items: center; gap: 0.5rem; }
.form input, .form select, .form textarea {
  padding: 0.45rem 0.6rem; border: 1px solid #d1d5db; border-radius: 6px; font-size: 0.9rem;
}
.form .row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
.form footer { display: flex; justify-content: flex-end; gap: 0.5rem; padding-top: 0.5rem; }
.form-error { background: #fee2e2; color: #991b1b; padding: 0.5rem 0.75rem; border-radius: 6px; font-size: 0.85rem; }
.muted { color: #9ca3af; }
.link { background: none; border: none; color: #2563eb; cursor: pointer; text-decoration: underline; padding: 0; font-size: 0.85rem; }
.speaker-picker { position: relative; }
.speaker-selected { font-size: 0.85rem; color: #4b5563; margin-top: 0.25rem; display: flex; gap: 0.5rem; align-items: center; }
.speaker-dropdown { position: absolute; left: 0; right: 0; top: 100%; margin-top: 4px; background: white; border: 1px solid #d1d5db; border-radius: 6px; box-shadow: 0 4px 14px rgba(0,0,0,0.08); z-index: 10; max-height: 240px; overflow-y: auto; }
.dropdown-item { display: block; width: 100%; text-align: left; background: white; border: none; padding: 0.5rem 0.75rem; cursor: pointer; border-bottom: 1px solid #f3f4f6; font-family: inherit; }
.dropdown-item:hover { background: #f9fafb; }
.dropdown-name { font-size: 0.9rem; color: #111827; font-weight: 500; }
.dropdown-meta { font-size: 0.75rem; color: #6b7280; }
.dropdown-status { padding: 0.5rem 0.75rem; color: #6b7280; font-size: 0.85rem; }
.dropdown-invite { display: block; width: 100%; text-align: left; background: #ecfdf5; color: #047857; border: none; padding: 0.5rem 0.75rem; cursor: pointer; font-family: inherit; font-size: 0.9rem; font-weight: 500; }
.dropdown-invite:hover { background: #d1fae5; }
</style>
